import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Player from '@vimeo/player';
import { ExternalLink, Loader2, Video } from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';
import {
  buildVimeoWebEmbedUrl,
  extractVimeoId,
  extractVimeoPrivacyHash,
  getPortraitPlayerSize,
  pickBestMp4File,
} from '@/lib/vimeo';

const DEFAULT_EMBED_PARAMS = {
  autoplay: true,
  loop: true,
  muted: false,
  controls: true,
  title: 0,
  byline: 0,
  portrait: 0,
  badge: 0,
  dnt: 1,
  playsinline: 1,
  responsive: 1,
};

export const VimeoEmbed = forwardRef(function VimeoEmbed({ vimeoLink, vimeoVideoId, thumbnailUrl, videoDuration }, ref) {
  const [mp4Url, setMp4Url] = useState(null);
  const [videoSize, setVideoSize] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [useIframeFallback, setUseIframeFallback] = useState(false);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);

  useImperativeHandle(ref, () => ({
    async getCurrentTime() {
      if (videoRef.current) return videoRef.current.currentTime;
      if (iframeRef.current) return new Player(iframeRef.current).getCurrentTime();
      return null;
    },
  }), []);

  const vimeoId = useMemo(
    () => vimeoVideoId || extractVimeoId(vimeoLink),
    [vimeoLink, vimeoVideoId]
  );

  const privacyHash = useMemo(() => extractVimeoPrivacyHash(vimeoLink), [vimeoLink]);

  const embedUrl = useMemo(() => {
    if (!vimeoId) return null;
    return buildVimeoWebEmbedUrl(vimeoId, DEFAULT_EMBED_PARAMS, privacyHash);
  }, [privacyHash, vimeoId]);

  const playerSize = useMemo(() => {
    if (videoSize?.width && videoSize?.height) {
      const isPortrait = videoSize.height > videoSize.width;
      if (isPortrait) {
        return getPortraitPlayerSize(320, videoSize.width, videoSize.height);
      }
      return { width: '100%', height: undefined, aspectRatio: '16 / 9' };
    }
    return getPortraitPlayerSize(320);
  }, [videoSize]);

  useEffect(() => {
    let cancelled = false;

    async function loadVideoSource() {
      if (!vimeoId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setLoadError(null);
      setMp4Url(null);

      try {
        const { data, error } = await supabase
          .from('videos')
          .select('width, height, available_files')
          .eq('vimeo_video_id', vimeoId)
          .maybeSingle();

        if (cancelled) return;
        if (error) throw error;

        if (data) {
          setVideoSize({ width: data.width, height: data.height });
          const bestFile = pickBestMp4File(data.available_files);
          if (bestFile?.link) {
            setMp4Url(bestFile.link);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || 'Could not load video source');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadVideoSource();

    return () => {
      cancelled = true;
    };
  }, [vimeoId]);

  if (!vimeoLink && !thumbnailUrl && !vimeoId) {
    return (
      <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
        <Video className="h-12 w-12 text-muted-foreground" />
        <p className="ml-4 text-muted-foreground">No video available for this exercise.</p>
      </div>
    );
  }

  const frameStyle = {
    width: typeof playerSize.width === 'number' ? `${playerSize.width}px` : playerSize.width,
    maxWidth: '100%',
    margin: '0 auto',
    backgroundColor: '#000',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    position: 'relative',
  };

  if (playerSize.height) {
    frameStyle.height = `${playerSize.height}px`;
  } else if (playerSize.aspectRatio) {
    frameStyle.aspectRatio = playerSize.aspectRatio;
  }

  return (
    <div className="space-y-2">
      <div style={frameStyle}>
        {loading ? (
          <div className="flex h-full min-h-[320px] items-center justify-center">
            <Loader2 className="h-8 w-8 text-muted-foreground animate-spin" />
          </div>
        ) : mp4Url && !useIframeFallback ? (
          <video
            key={mp4Url}
            ref={videoRef}
            src={mp4Url}
            poster={thumbnailUrl || undefined}
            controls
            loop
            playsInline
            className="h-full w-full object-contain bg-black"
            onError={() => setUseIframeFallback(true)}
          />
        ) : embedUrl ? (
          <iframe
            ref={iframeRef}
            src={embedUrl}
            title="Exercise Video"
            className="h-full w-full border-0 bg-black"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <p className="text-sm text-muted-foreground">Could not build a Vimeo player for this exercise.</p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
        <span>
          {videoDuration ? `${videoDuration}s` : null}
          {loadError ? ` · ${loadError}` : null}
        </span>
        {vimeoLink ? (
          <a
            href={vimeoLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          >
            Open on Vimeo
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>
    </div>
  );
});
